'use client';

import {ModalBackdrop} from 'common/components/modal_backdrop';
import {useAuth} from 'iq-auth';
import {AuthForm} from 'iq-auth/components/auth_form';
import {FlowTakehomeUpload} from 'iq-interview_experiences/components/flow_takehome_upload';
import type {TakeHomeFile} from 'iq-interview_experiences/types';
import {generateSubmissionFlowId} from 'common/submission_flow_id';
import {GradingProgressModal} from 'iq-takehomes/components/grading_progress_modal';
import {getTakehomeUploadUrl, gradeTakeHome} from 'iq-takehomes/api';
import {trackTakeHomeGradeSubmitted} from 'iq-takehomes/tracking';
import {X} from 'lucide-react';
import {useRouter} from 'next/navigation';
import {useState} from 'react';

type Props = {
  slug: string;
  onCancel?: () => void;
};

export function GradePanel({slug, onCancel}: Props) {
  const router = useRouter();
  const {user, isReady} = useAuth();
  const [gradeFlowId] = useState(() => generateSubmissionFlowId());
  const [file, setFile] = useState<TakeHomeFile | null>(null);
  const [grading, setGrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);

  async function handleAddFiles(incoming: File[]) {
    const f = incoming[0];
    if (!f) return;

    const entry: TakeHomeFile = {
      id: `${f.name}-${f.size}-${Date.now()}`,
      name: f.name,
      size: f.size,
      s3Key: '',
      category: 'solution' as const,
      uploading: true,
      error: null,
    };
    setFile(entry);
    setError(null);

    try {
      const {uploadUrl, s3Key, fields} = await getTakehomeUploadUrl(f.name, f.type || 'application/pdf', gradeFlowId, slug);
      const form = new FormData();
      for (const [key, value] of Object.entries(fields)) form.append(key, value);
      form.append('file', f);
      const res = await fetch(uploadUrl, {method: 'POST', body: form});
      if (!res.ok) {
        const msg = res.status === 403 ? 'File is too large. Maximum size is 25 MB.' : 'Upload failed. Please try again.';
        setFile((prev) => (prev ? {...prev, uploading: false, error: msg} : null));
        return;
      }
      setFile((prev) => (prev ? {...prev, uploading: false, s3Key} : null));
    } catch {
      setFile((prev) =>
        prev ? {...prev, uploading: false, error: 'Upload failed. Please try again.'} : null,
      );
    }
  }

  function handleRemoveFile() {
    setFile(null);
    setError(null);
  }

  async function handleGrade() {
    if (!file?.s3Key) return;
    setGrading(true);
    setError(null);
    try {
      const res = await gradeTakeHome(slug, file.s3Key, gradeFlowId);
      if (!res?.solution_id) {
        const reason = (res as {error?: string})?.error;
        setError(reason || 'Something went wrong. Please try again.');
        setGrading(false);
        return;
      }
      void trackTakeHomeGradeSubmitted(slug);
      router.push(`/takehomes/${slug}/solutions/${res.solution_id}`);
    } catch {
      setError('Something went wrong. Please try again.');
      setGrading(false);
    }
  }

  if (isReady && !user.loggedIn) {
    return (
      <div className='bg-white border border-iq-grey-light rounded-xl p-5 flex flex-col gap-4'>
        <div>
          <h3 className='text-base font-semibold text-iq-black mb-1'>Grade your take-home</h3>
          <p className='text-sm text-iq-grey-medium leading-relaxed'>
            Sign in or create a free account to upload your solution and get AI feedback.
          </p>
        </div>
        <button
          type='button'
          onClick={() => setAuthOpen(true)}
          className='w-full py-2.5 bg-iq-red text-white text-sm font-semibold rounded-lg transition-opacity hover:opacity-90'>
          Grade my take-home
        </button>
        {authOpen && (
          <ModalBackdrop onClose={() => setAuthOpen(false)} aria-label='Sign in or create account'>
            <div className='relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6'>
              <button
                type='button'
                onClick={() => setAuthOpen(false)}
                className='absolute top-4 right-4 text-iq-grey-medium hover:text-iq-grey-dark transition-colors'
                aria-label='Close'>
                <X className='w-4 h-4' />
              </button>
              <AuthForm
                onSuccess={() => {
                  setAuthOpen(false);
                  router.refresh();
                }}
              />
            </div>
          </ModalBackdrop>
        )}
      </div>
    );
  }

  const isUploading = file?.uploading ?? false;
  const canGrade = !isUploading && !grading && !!file?.s3Key && !file.error;

  return (
    <div className='bg-white border border-iq-grey-light rounded-xl p-5 flex flex-col gap-4'>
      <div>
        {onCancel && (
          <button
            type='button'
            onClick={onCancel}
            disabled={grading}
            className='text-xs text-iq-grey-medium hover:text-iq-black transition-colors mb-2 disabled:opacity-40'>
            ← Back to my scores
          </button>
        )}
        <h3 className='text-base font-semibold text-iq-black mb-1'>Grade your take-home</h3>
        <p className='text-sm text-iq-grey-medium leading-relaxed'>
          Upload your completed solution and get feedback on your work.
        </p>
      </div>

      <FlowTakehomeUpload
        files={file ? [file] : []}
        onAddFiles={handleAddFiles}
        onRemoveFile={handleRemoveFile}
        acceptedExtensions={new Set(['.pdf'])}
        disabled={grading}
      />

      {error && <p className='text-sm text-iq-red'>{error}</p>}

      <button
        type='button'
        disabled={!canGrade}
        onClick={handleGrade}
        className='w-full py-2.5 bg-iq-red text-white text-sm font-semibold rounded-lg transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed'>
        {grading ? 'Grading your solution…' : 'Grade my solution'}
      </button>

      {grading && <GradingProgressModal />}
    </div>
  );
}
